import * as React from 'react';
// Side-effect import: registers the <baton-pricing-calculator> custom
// element (brief 110, shared in @relaystation-v3/web-elements). The
// marketing site registers the same element the same way.
import '@relaystation-v3/web-elements/baton-pricing-calculator';
import type { BatonShapeChangeDetail } from '@relaystation-v3/web-elements/baton-pricing-calculator';

// The element is framework-agnostic; teach TSX/React about its tag +
// the two kebab-case attributes it accepts.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'baton-pricing-calculator': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & { 'api-base'?: string; 'curl-base'?: string };
    }
  }
}

/**
 * React wrapper around <baton-pricing-calculator>. Same-origin by
 * default (`api-base` unset → the element fetches `/v1/baton/prices` +
 * `/v1/baton/quote` relative to app.relaystation.ai, which proxies /v1/*
 * to the API — no CORS, no auth). `onShapeChange` surfaces the user's
 * current selection (brief 112's additive readback API) so the create
 * flow can build the `POST /v1/baton` body.
 */
export function BatonPricingCalculator({
  onShapeChange,
}: {
  onShapeChange?: (detail: BatonShapeChangeDetail) => void;
}) {
  const ref = React.useRef<HTMLElement>(null);
  const cb = React.useRef(onShapeChange);
  cb.current = onShapeChange;

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e: Event) => {
      cb.current?.((e as CustomEvent<BatonShapeChangeDetail>).detail);
    };
    el.addEventListener('baton-shape-change', handler);
    return () => el.removeEventListener('baton-shape-change', handler);
  }, []);

  return (
    <baton-pricing-calculator ref={ref} curl-base="https://api.relaystation.ai" />
  );
}
